import logger from './lib/logger';
import React, { useState, useRef, useCallback } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import { AppProvider, useApp } from './context/AppContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ProgressProvider } from './context/ProgressContext';
import MenuBar from './components/Editor/MenuBar';
import Toolbar from './components/Editor/Toolbar';
import Viewport from './components/Editor/Viewport';
import LibraryPanel from './components/Panels/LibraryPanel';
import PropertiesPanel from './components/Panels/PropertiesPanel';
import SceneSidePanel from './components/Panels/SceneSidePanel';
import ProjectorPOVWindowsLayer from './components/Panels/ProjectorPOVWindowsLayer';
import ProjectsModal from './components/Modals/ProjectsModal';
import ExportModal from './components/Modals/ExportModal';
import UserManualModal from './components/Help/UserManualModal';
import GuidedTour from './components/Help/GuidedTour';
import ViewerPage from './components/Viewer/ViewerPage';
import LoginPage from './components/Auth/LoginPage';
import ForgotPasswordPage from './components/Auth/ForgotPasswordPage';
import ResetPasswordPage from './components/Auth/ResetPasswordPage';
import SignupPage from './components/Auth/SignupPage';
import PurchasePage from './components/Auth/PurchasePage';
import LandingPage from './components/Landing/LandingPage';
import LandingPageV2 from './components/Landing/LandingPageV2';

// Standalone utility page — lazy-loaded so its bundle never touches the editor.
const TestPatternGenerator = React.lazy(() => import('./components/Tools/TestPatternGenerator'));
const AvDesigner = React.lazy(() => import('./components/Tools/AvDesigner/AvDesigner'));
const AvdShareView = React.lazy(() => import('./components/Tools/AvDesigner/AvdShareView'));
const TimerView = React.lazy(() => import('./components/Timer/TimerView'));
const LedPattern = React.lazy(() => import('./components/Tools/LedPattern/LedPattern'));
import TermsPage from './components/Legal/TermsPage';
import AdminPanel from './components/Admin/AdminPanel';
import UserSettings from './components/Settings/UserSettings';
import { Button } from './components/ui/button';
import { ChevronLeft, ChevronRight, Projector, Frame, Anchor, Plus, AlertTriangle, RotateCcw, Settings, Shield, LogOut } from 'lucide-react';
import { filesApi } from './lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './components/ui/dialog';
import { Switch } from './components/ui/switch';

// Global Add Projector Dialog Component
const AddProjectorDialog = () => {
  const {
    addProjectorDialogOpen,
    setAddProjectorDialogOpen,
    addProjectorSpec,
    addWithFrame,
    setAddWithFrame,
    addWithClamp,
    setAddWithClamp,
    addAsReverseProjector,
    setAddAsReverseProjector,
    handleAddProjectorWithOptions
  } = useApp();

  if (!addProjectorSpec) return null;

  return (
    <Dialog open={addProjectorDialogOpen} onOpenChange={setAddProjectorDialogOpen}>
      <DialogContent className="bg-zinc-900 border-zinc-700 max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Projector className="w-5 h-5 text-fuchsia-400" />
            Add {addProjectorSpec?.brand} {addProjectorSpec?.model}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-2">
          {/* Projector Info */}
          <div className="p-3 bg-zinc-800 rounded-lg space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-zinc-400">Brightness</span>
              <span>{addProjectorSpec?.brightness?.toLocaleString()} lm</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-zinc-400">Resolution</span>
              <span>{addProjectorSpec?.resolution_width}×{addProjectorSpec?.resolution_height}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-zinc-400">Size</span>
              <span>{addProjectorSpec?.width}×{addProjectorSpec?.height}×{addProjectorSpec?.depth}mm</span>
            </div>
          </div>
          
          {/* Reverse Projector Option */}
          <div className={`flex items-center justify-between p-3 rounded-lg ${addAsReverseProjector ? 'bg-amber-900/30 border border-amber-500/50' : 'bg-zinc-800'}`}>
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded flex items-center justify-center ${addAsReverseProjector ? 'bg-amber-500/30' : 'bg-fuchsia-500/20'}`}>
                <RotateCcw className={`w-4 h-4 ${addAsReverseProjector ? 'text-amber-400' : 'text-fuchsia-400'}`} />
              </div>
              <div>
                <div className="text-sm font-medium">Reverse Projector</div>
                <div className="text-[10px] text-zinc-500">Reveals object's UV-mapped texture</div>
              </div>
            </div>
            <Switch
              checked={addAsReverseProjector}
              onCheckedChange={setAddAsReverseProjector}
              data-testid="add-reverse-projector-toggle"
            />
          </div>
          
          {/* Frame Option */}
          <div className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded bg-fuchsia-500/20 flex items-center justify-center">
                <Frame className="w-4 h-4 text-fuchsia-400" />
              </div>
              <div>
                <div className="text-sm font-medium">Rigging Frame</div>
                <div className="text-[10px] text-zinc-500">Tube cage frame for mounting</div>
              </div>
            </div>
            <Switch
              checked={addWithFrame}
              onCheckedChange={setAddWithFrame}
              data-testid="add-with-frame-toggle"
            />
          </div>
          
          {/* Clamp Option */}
          <div className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded bg-fuchsia-500/20 flex items-center justify-center">
                <Anchor className="w-4 h-4 text-fuchsia-400" />
              </div>
              <div>
                <div className="text-sm font-medium">Rigging Clamp</div>
                <div className="text-[10px] text-zinc-500">Truss clamp for hanging</div>
              </div>
            </div>
            <Switch
              checked={addWithClamp}
              onCheckedChange={setAddWithClamp}
              data-testid="add-with-clamp-toggle"
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => setAddProjectorDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleAddProjectorWithOptions} className="bg-fuchsia-600 hover:bg-fuchsia-700">
            <Plus className="w-4 h-4 mr-2" />
            Add to Scene
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const EditorLayout = () => {
  const { 
    leftPanelOpen, 
    setLeftPanelOpen, 
    rightPanelOpen, 
    setRightPanelOpen,
    addSceneObject,
    uploadedFiles,
    setUploadedFiles,
    setActiveLeftTab,
    isDirty,
    saveProject,
    setIsDirty,
    sceneObjects,
    projectors,
    newProject
  } = useApp();
  
  const [projectsModalOpen, setProjectsModalOpen] = useState(false);
  const [projectsModalMode, setProjectsModalMode] = useState('open');
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportType, setExportType] = useState('image');
  const [unsavedChangesAction, setUnsavedChangesAction] = useState(null); // 'new' or 'open'
  const [unsavedChangesDialogOpen, setUnsavedChangesDialogOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [activeTourId, setActiveTourId] = useState(null);
  const fileInputRef = useRef(null);
  
  // Check if scene has any content
  const hasSceneContent = sceneObjects.length > 0 || projectors.length > 0;

  const handleNewProject = () => {
    // Always show warning if there's any content in the scene
    if (hasSceneContent || isDirty) {
      setUnsavedChangesAction('new');
      setUnsavedChangesDialogOpen(true);
    } else {
      setProjectsModalMode('new');
      setProjectsModalOpen(true);
    }
  };

  const handleOpenProjects = () => {
    // Always show warning if there's any content in the scene
    if (hasSceneContent || isDirty) {
      setUnsavedChangesAction('open');
      setUnsavedChangesDialogOpen(true);
    } else {
      setProjectsModalMode('open');
      setProjectsModalOpen(true);
    }
  };
  
  const proceedWithUnsavedAction = () => {
    setUnsavedChangesDialogOpen(false);
    // Clear the scene before proceeding
    newProject();
    if (unsavedChangesAction === 'new') {
      setProjectsModalMode('new');
      setProjectsModalOpen(true);
    } else if (unsavedChangesAction === 'open') {
      setProjectsModalMode('open');
      setProjectsModalOpen(true);
    }
  };
  
  const saveAndProceed = async () => {
    try {
      await saveProject();
      toast.success('Project saved');
      setUnsavedChangesDialogOpen(false);
      // Clear the scene before proceeding
      newProject();
      if (unsavedChangesAction === 'new') {
        setProjectsModalMode('new');
        setProjectsModalOpen(true);
      } else if (unsavedChangesAction === 'open') {
        setProjectsModalMode('open');
        setProjectsModalOpen(true);
      }
    } catch (error) {
      logger.error('Save error:', error);
      toast.error('Failed to save project. Please try again.');
      // Don't proceed if save failed - let user decide
    }
  };

  const handleExport = (type) => {
    setExportType(type);
    setExportModalOpen(true);
  };

  const handleImportFile = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const fileName = file.name;
    const ext = fileName.split('.').pop().toLowerCase();
    
    // Validate file type
    const allowedExtensions = ['obj', 'fbx', 'glb', 'gltf'];
    if (!allowedExtensions.includes(ext)) {
      toast.error(`Unsupported file type: .${ext}. Supported: OBJ, FBX, GLB, GLTF`);
      e.target.value = '';
      return;
    }
    
    try {
      // Upload to backend to store in Files library only (don't auto-add to scene)
      toast.info('Uploading file...');
      const response = await filesApi.upload(file);
      const uploadedFile = response.data;
      
      // Add to uploaded files list
      setUploadedFiles(prev => [...prev, uploadedFile]);
      
      // Switch to Files tab to show the uploaded file
      setLeftPanelOpen(true);
      setActiveLeftTab('files');
      
      toast.success(`${fileName} uploaded to Files library. Click "Add to Scene" to use it.`);
    } catch (error) {
      logger.error('Failed to upload file:', error);
      toast.error('Failed to upload file: ' + (error.response?.data?.detail || error.message));
    }
    
    e.target.value = '';
  }, [setUploadedFiles, setLeftPanelOpen, setActiveLeftTab]);

  return (
    <div className="iav-app" data-testid="iav-app">
      {/* Menu Bar */}
      <MenuBar 
        onNewProject={handleNewProject}
        onOpenProjects={handleOpenProjects}
        onExport={handleExport}
        onOpenManual={() => setManualOpen(true)}
      />
      
      {/* Toolbar */}
      <Toolbar 
        onNewProject={handleNewProject}
        onOpenProjects={handleOpenProjects}
        onImportFile={handleImportFile}
      />
      
      {/* Main Editor Area */}
      <div className="editor-layout">
        {/* Left Panel Toggle */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute left-0 top-1/2 -translate-y-1/2 z-20 h-12 w-6 rounded-none rounded-r bg-zinc-800/80 hover:bg-zinc-700"
          onClick={() => setLeftPanelOpen(!leftPanelOpen)}
          data-testid="toggle-left-panel"
        >
          {leftPanelOpen ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </Button>

        {/* Left Panel - Library */}
        {leftPanelOpen && <LibraryPanel />}
        
        {/* Viewport */}
        <Viewport />
        
        {/* Scene Side Panel - right side before properties */}
        <SceneSidePanel />

        {/* Projector POV floating preview windows (live RTT-rendered) */}
        <ProjectorPOVWindowsLayer />
        
        {/* Right Panel - Properties */}
        {rightPanelOpen && <PropertiesPanel />}

        {/* Right Panel Toggle */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-0 top-1/2 -translate-y-1/2 z-20 h-12 w-6 rounded-none rounded-l bg-zinc-800/80 hover:bg-zinc-700"
          onClick={() => setRightPanelOpen(!rightPanelOpen)}
          data-testid="toggle-right-panel"
        >
          {rightPanelOpen ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </Button>
      </div>

      {/* Hidden file input for importing */}
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept=".obj,.fbx,.gltf,.glb"
        onChange={handleFileChange}
      />

      {/* Modals */}
      <ProjectsModal 
        open={projectsModalOpen} 
        onClose={() => setProjectsModalOpen(false)}
        mode={projectsModalMode}
      />
      <ExportModal 
        open={exportModalOpen} 
        onClose={() => setExportModalOpen(false)}
        type={exportType}
      />

      {/* User Manual */}
      <UserManualModal
        isOpen={manualOpen}
        onClose={() => setManualOpen(false)}
        onStartTour={(tourId) => setActiveTourId(tourId)}
      />

      {/* Guided Tour */}
      {activeTourId && (
        <GuidedTour
          tourId={activeTourId}
          onEnd={() => setActiveTourId(null)}
        />
      )}
      
      {/* Unsaved Changes Warning Dialog */}
      <Dialog open={unsavedChangesDialogOpen} onOpenChange={setUnsavedChangesDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-400">
              <AlertTriangle className="w-5 h-5" />
              {unsavedChangesAction === 'new' ? 'Create New Project?' : 'Open Another Project?'}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {unsavedChangesAction === 'new' 
                ? 'Creating a new project will clear all current objects (projectors, 3D models, etc.).'
                : 'Opening another project will replace your current work.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 text-sm text-zinc-300">
            <p className="mb-2">Current scene contains:</p>
            <ul className="list-disc list-inside text-zinc-400">
              {projectors.length > 0 && <li>{projectors.length} projector(s)</li>}
              {sceneObjects.length > 0 && <li>{sceneObjects.length} object(s)</li>}
            </ul>
            <p className="mt-3 text-amber-400/80">These will be lost if you don&apos;t save first.</p>
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => setUnsavedChangesDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={proceedWithUnsavedAction}
            >
              Discard & Continue
            </Button>
            <Button 
              className="bg-fuchsia-600 hover:bg-fuchsia-700"
              onClick={saveAndProceed}
            >
              Save First
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Global Add Projector Dialog */}
      <AddProjectorDialog />
    </div>
  );
};

const WelcomeScreen = () => {
  const { loadProject } = useApp();
  const [projectsModalOpen, setProjectsModalOpen] = useState(false);
  const [projectsModalMode, setProjectsModalMode] = useState('open');

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center" data-testid="welcome-screen">
      <div className="text-center max-w-lg">
        <div className="w-20 h-20 bg-fuchsia-500 rounded-xl mx-auto mb-6 flex items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 7l7 7-7 7"/>
            <path d="M19 5v14"/>
          </svg>
        </div>
        <h1 className="text-4xl font-bold mb-2 tracking-tight">IAV</h1>
        <p className="text-zinc-400 mb-8">Projection Mapping Simulator</p>
        
        <div className="flex flex-col gap-3">
          <Button 
            size="lg"
            className="bg-fuchsia-600 hover:bg-fuchsia-500 text-base"
            onClick={() => {
              setProjectsModalMode('new');
              setProjectsModalOpen(true);
            }}
            data-testid="welcome-new-project-btn"
          >
            Create New Project
          </Button>
          <Button 
            variant="outline" 
            size="lg"
            className="border-zinc-700 hover:bg-zinc-800 text-base"
            onClick={() => {
              setProjectsModalMode('open');
              setProjectsModalOpen(true);
            }}
            data-testid="welcome-open-project-btn"
          >
            Open Existing Project
          </Button>
        </div>

        <p className="text-xs text-zinc-600 mt-8">
          Plan and pre-visualize complex projection mapping projects
        </p>
      </div>

      <ProjectsModal 
        open={projectsModalOpen} 
        onClose={() => setProjectsModalOpen(false)}
        mode={projectsModalMode}
      />
    </div>
  );
};

const MainApp = () => {
  return <EditorLayout />;
};

// Auth gate: only show app if authenticated
const AuthGate = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-violet-500/30 border-t-violet-500 rounded-full animate-spin mx-auto mb-3" />
          <p className="text-zinc-400 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  // Unauthenticated users land on the public marketing page at "/" instead
  // of the plain login form. /login still exists for people who know where
  // they want to go. The landing's CTAs link to /login and /signup.
  if (!isAuthenticated) return <Navigate to="/" replace />;

  return (
    <AppProvider>
      <ProgressProvider>
        <Routes>
          {/* Simulation editor — the 3D Babylon scene (subscription-gated) */}
          <Route path="/app" element={<SimGate><MainApp /></SimGate>} />
          <Route path="/simulation" element={<Navigate to="/app" replace />} />
          <Route path="/admin" element={<AdminRoute />} />
          <Route path="/settings" element={<UserSettings />} />
          {/* Anything else auth-only falls back to the home page */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ProgressProvider>
    </AppProvider>
  );
};

// Public root: the home page. Always shows LandingPageV2 — even for
// authenticated users — so that after sign-in the user lands back here
// and can choose between the Simulation editor and the Test Pattern tool.
const PublicRoot = () => {
  const { isLoading } = useAuth();
  if (isLoading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-violet-500/30 border-t-violet-500 rounded-full animate-spin" />
      </div>
    );
  }
  return <LandingPageV2 />;
};

// Admin-only route guard
const AdminRoute = () => {
  const { isAdmin } = useAuth();
  if (!isAdmin) return <Navigate to="/" replace />;
  return <AdminPanel />;
};

// AV System Designer — a FREE workspace (like Pattern & Blend / LED Pattern).
// Requires sign-in (it has cloud save + share), but NOT an active
// subscription, so demo / expired users keep full access. Only the 3D
// Simulation is subscription-gated (see SimGate).
const AvdGate = ({ children }) => {
  const { isLoading, isAuthenticated } = useAuth();
  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', background: '#0a0a0f', color: '#e6e6f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui' }}>
        <div style={{ width: 32, height: 32, border: '2px solid rgba(124,92,255,0.3)', borderTopColor: '#7c5cff', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: '/tools/av-designer' }} />;
  }
  return children;
};

// Gate the 3D Simulation behind an ACTIVE subscription (15-day demo or paid).
// When the demo lapses and the user hasn't subscribed, we bounce them to the
// paywall. All other tools stay open.
const SimGate = ({ children }) => {
  const { isSubscriptionActive } = useAuth();
  if (!isSubscriptionActive()) {
    return <Navigate to="/purchase" replace state={{ from: '/app', reason: 'sim-access' }} />;
  }
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/purchase" element={<PurchasePage />} />
          <Route path="/pricing" element={<PurchasePage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/landing-preview" element={<LandingPage />} />
          <Route path="/landing-v2" element={<LandingPageV2 />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/sim-share/:shareToken" element={<ViewerPage />} />
          <Route
            path="/tools/test-pattern"
            element={
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#0a0a0f', color: '#e6e6f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui' }}>Loading Pattern & Blend…</div>}>
                <TestPatternGenerator />
              </React.Suspense>
            }
          />
          <Route
            path="/tools/av-designer"
            element={
              <AvdGate>
                <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#0a0a0f', color: '#e6e6f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui' }}>Loading AV System Designer…</div>}>
                  <AvDesigner />
                </React.Suspense>
              </AvdGate>
            }
          />
          <Route
            path="/tools/led-pattern"
            element={
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#0a0a0f', color: '#e6e6f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui' }}>Loading LED Pattern…</div>}>
                <LedPattern />
              </React.Suspense>
            }
          />
          <Route
            path="/share/:token"
            element={
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#0a0a0f', color: '#e6e6f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui' }}>Loading shared project…</div>}>
                <AvdShareView />
              </React.Suspense>
            }
          />
          <Route
            path="/viewer/:token"
            element={
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#0a0a0f', color: '#e6e6f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui' }}>Loading shared project…</div>}>
                <AvdShareView />
              </React.Suspense>
            }
          />
          <Route
            path="/editor/:token"
            element={
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#0a0a0f', color: '#e6e6f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui' }}>Loading shared project…</div>}>
                <AvdShareView />
              </React.Suspense>
            }
          />
          <Route
            path="/timer/:code"
            element={
              <React.Suspense fallback={<div style={{ minHeight: '100vh', background: '#000' }} />}>
                <TimerView />
              </React.Suspense>
            }
          />
          <Route path="/" element={<PublicRoot />} />
          <Route path="/*" element={<AuthGate />} />
        </Routes>
        <Toaster position="bottom-center" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
